import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ProjectSalesTaxWarningModal from '../components/Projects/ProjectSalesTaxWarningModal';
import API_BASE from '../utils/api.js';
import { hasProjectSalesTaxAssigned } from '../utils/projectSalesTax.js';

export default function TimeBillingInvoiceFormPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const userId = localStorage.getItem('userId');
  const apiUrl = API_BASE;

  // Only trust keys, never full objects.
  const entryKeys = location.state?.entryKeys || [];

  const [status, setStatus] = useState('Generating invoice...');
  const taxAlertShownRef = useRef(false);
  const [showTaxWarning, setShowTaxWarning] = useState(false);
  const [project, setProject] = useState(null);

  useEffect(() => {
    if (!entryKeys.length) {
      setStatus('No selected entries found.');
      return;
    }

    const createInvoice = async () => {
      try {
        const token = localStorage.getItem('idToken');
        const [projectRes, settingsRes, qbStatusRes] = await Promise.all([
          fetch(
            `${apiUrl}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
            { cache: 'no-store' }
          ),
          fetch(`${apiUrl}/api/settings/${userId}`),
          fetch(`${apiUrl}/api/quickbooks/status`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const nextProject = projectRes.ok ? await projectRes.json() : null;
        setProject(nextProject);

        const qbSettings = settingsRes.ok ? (await settingsRes.json())?.quickbooks : null;
        const qbStatus = qbStatusRes.ok ? await qbStatusRes.json().catch(() => ({})) : {};
        const qbConnected = Boolean(qbStatus?.connected);

        if (!taxAlertShownRef.current) {
          taxAlertShownRef.current = true;
          if (!hasProjectSalesTaxAssigned(nextProject)) {
            setShowTaxWarning(true);
          }
        }

        /* --------------------------------------------------------
           1. FETCH FRESH ENTRIES (SOURCE OF TRUTH)
        -------------------------------------------------------- */
        const resEntries = await fetch(
          `${apiUrl}/api/timebilling/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`
        );

        const allEntries = await resEntries.json();

        const selectedEntries = allEntries.filter((e) => entryKeys.includes(e.sortKey));

        if (!selectedEntries.length) {
          setStatus('Selected entries no longer exist.');
          return;
        }

        /* --------------------------------------------------------
           2. BUILD PAYLOAD (INCLUDES TAXABLE)
        -------------------------------------------------------- */
        const payload = {
          userId,
          projectId,
          entries: selectedEntries.map((e) => ({
            sortKey: e.sortKey,
            description: e.description || '',
            billableHours: Number(e.billableHours || 0),
            rate: Number(e.rate || 0),
            activity: e.activity || 'General',
            employee: e.employee || '',
            taxable: e.taxable !== false,
          })),
        };

        /* --------------------------------------------------------
           3. CREATE INVOICE
        -------------------------------------------------------- */
        const res = await fetch(`${apiUrl}/api/timebilling-invoices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok) {
          console.error('Invoice creation failed:', data);
          setStatus(data.error || 'Failed to create invoice.');
          return;
        }

        /* --------------------------------------------------------
           4. QB AUTO-SYNC + REFRESH + REDIRECT
        -------------------------------------------------------- */
        if (qbConnected && qbSettings?.autoCreateOnSave && token) {
          fetch(`${apiUrl}/api/quickbooks/create-timebilling-invoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ tbInvoiceId: data.tbInvoiceId, projectId }),
          }).catch(() => {});
        }

        document.dispatchEvent(new Event('timebilling-updated'));

        navigate(`/projects/${projectId}/timebilling-invoices/${data.tbInvoiceId}`, {
          replace: true,
        });
      } catch (err) {
        console.error('TB invoice creation error:', err);
        setStatus('An unexpected error occurred.');
      }
    };

    createInvoice();
  }, [entryKeys, projectId, userId, apiUrl, navigate]);

  return (
    <div style={{ padding: 24 }}>
      <h2>Generating Time Billing Invoice...</h2>
      <p>{status}</p>
      <ProjectSalesTaxWarningModal
        isOpen={showTaxWarning}
        docLabel="time billing invoice"
        onClose={() => setShowTaxWarning(false)}
        onEditProject={() =>
          navigate(`/projects/${encodeURIComponent(projectId)}/edit`, {
            state: { project },
          })
        }
      />
    </div>
  );
}
